/**
 * Business-event tracking derived from Vuex actions.
 *
 * Observes actions via `store.subscribeAction({ after })` (Vuex >= 3.1) — pure
 * observation, ZERO changes to components. The subscriber body is fully guarded
 * so a throw can never propagate into Vuex's dispatch loop and break an action.
 *
 * The action PAYLOAD is intentionally never sent (it may contain PII or be
 * large): only the action type is turned into a category/name.
 */
import { track } from './tracking'

// Actions we never want to track (too noisy / internal). Extend as needed.
const DENYLIST = []

// Optional enrichment for high-value actions:
//   type -> (payload) => [category, action, name]
const ACTION_MAP = {
  selectAssetClass: (p) => ['AssetClass', 'select', typeof p === 'string' ? p : undefined],
}

export function installStoreTracking(store) {
  try {
    if (!store || typeof store.subscribeAction !== 'function') return

    store.subscribeAction({
      after: (action) => {
        try {
          const type = action && action.type
          if (!type || DENYLIST.indexOf(type) !== -1) return

          if (ACTION_MAP[type]) {
            const e = ACTION_MAP[type](action.payload)
            track.event(e[0], e[1], e[2])
            return
          }

          // Generic coverage: namespaced "module/action" → category "module",
          // event name "action". Payload deliberately dropped.
          const idx = type.indexOf('/')
          const category = idx > -1 ? type.slice(0, idx) : 'Store'
          const name = idx > -1 ? type.slice(idx + 1) : type
          track.event(category, 'action', name)
        } catch (e) {
          /* swallow — never impact the dispatch loop */
        }
      },
    })
  } catch (e) {
    /* swallow */
  }
}
